/**
 * Workflows state: the organization's workflows, one workflow with its recent
 * runs, and one run with its steps, from the workspace server's
 * `/api/amoreg/workflows/*` routes (`apps/server/src/amoreg/workflows/
 * http.ts`), where the tenant guard lives. Nothing here decides who may do
 * what.
 *
 * Reads are per-organization atoms on the compliance stream
 * (`complianceQueryAtom`): the runner announces every step that moves, so an
 * open chart updates as the run works; a page holding an active run also
 * re-reads on a slow timer as a safety net. A read answers `ready`,
 * `missing` (404: not this organization's, or deleted) or `unavailable`.
 *
 * One open chart's copy of a workflow is `useWorkflowDoc`: the reducer in
 * `./workflowDoc.ts`, fed by the presence room, its own saves and the read.
 *
 * @module state/workflows
 */
import {
  WorkflowDetailResponse,
  WorkflowGraphIssue,
  WorkflowListResponse,
  WorkflowRunResponse,
  WorkflowScheduleView,
  WorkflowSuggestFieldsResponse,
  WorkflowIntakeBrowseResponse,
  WorkflowMailboxAccessResponse,
  WorkflowSharing,
  WorkflowLockResponse,
  WorkflowRevisionsSinceResult,
  WorkflowRequestPointers,
  WorkflowUndoResult,
  WorkflowChat,
  WorkflowChatsResponse,
  Workflow as WorkflowSchema,
  type Workflow,
  type WorkflowDetailResponse as WorkflowDetail,
  type WorkflowGraph,
  type WorkflowGraphIssue as GraphIssue,
  type WorkflowDecideRequest,
  type WorkflowLockRequest,
  type WorkflowMailboxAccessRequest,
  type WorkflowRun,
  type WorkflowRunFile,
  type WorkflowScheduleRequest,
  type WorkflowSuggestFieldsRequest,
  type WorkflowIntakeBrowseResponse as IntakeBrowseResponse,
  type WorkflowSummary,
} from "@amoreg/contracts";
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { complianceQueryAtom } from "./complianceLive";
import {
  type Content,
  initialDoc,
  nextTick,
  reduceWorkflowDoc,
  saveDelay,
  saveStatus,
  type SaveStatus,
  unsavedBody,
  type WorkflowDocEffect,
  type WorkflowDocEvent,
  type WorkflowDocNotice,
  type WorkflowDocState,
} from "./workflowDoc";
import type { WorkflowPresenceView } from "./workflowPresence";

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export type WorkflowRead<A> =
  | { readonly kind: "ready"; readonly value: A }
  | { readonly kind: "missing" }
  | { readonly kind: "unavailable" };

export const workflowsUrl = (path: string, params: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/workflows/${path}`, params);

const TRANSIENT = new Set([502, 503, 504]);

/** One read (Workflows and Browser tasks share it): 404 is "missing"; a transient gateway answer is retried twice; anything else undecodable is "unavailable". */
export const readWorkflows = <A>(url: string, decode: (input: unknown) => A): Effect.Effect<WorkflowRead<A>> =>
  Effect.tryPromise({
    try: async (signal) => {
      const response = await fetch(url, { credentials: "include", signal });
      if (TRANSIENT.has(response.status)) throw new Error("transient");
      if (response.status === 404) return { kind: "missing" } as const;
      if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
        return { kind: "unavailable" } as const;
      }
      try {
        return { kind: "ready", value: decode(await response.json()) } as const;
      } catch {
        return { kind: "unavailable" } as const;
      }
    },
    catch: (cause) => cause,
  }).pipe(
    Effect.retry({ times: 2 }),
    Effect.orElseSucceed((): WorkflowRead<A> => ({ kind: "unavailable" })),
  );

const decodeList = Schema.decodeUnknownSync(WorkflowListResponse);
const decodeDetail = Schema.decodeUnknownSync(WorkflowDetailResponse);
const decodeRun = Schema.decodeUnknownSync(WorkflowRunResponse);
const decodeWorkflow = Schema.decodeUnknownSync(Schema.Struct({ workflow: WorkflowSchema }));
const decodeSchedule = Schema.decodeUnknownSync(Schema.Struct({ schedule: Schema.NullOr(WorkflowScheduleView) }));
const decodeIssues = Schema.decodeUnknownSync(Schema.Struct({ message: Schema.String, issues: Schema.Array(WorkflowGraphIssue) }));

const none = <A>(): Effect.Effect<WorkflowRead<A>> => Effect.succeed({ kind: "unavailable" });

/** `spaceId`: undefined for every workflow, "" for organization-wide only, or one space's id. */
const listKey = (organizationId: string, spaceId: string | undefined) =>
  `${organizationId}\u0000${spaceId === undefined ? "*" : spaceId}`;

const listAtom = Atom.family((key: string) => {
  const [organizationId = "", space = "*"] = key.split("\u0000");
  const params: Record<string, string> = { organizationId };
  if (space !== "*") params.spaceId = space;
  return complianceQueryAtom(
    organizationId,
    organizationId
      ? readWorkflows(workflowsUrl("list", params), (json) => decodeList(json).workflows)
      : none<ReadonlyArray<WorkflowSummary>>(),
  ).pipe(Atom.withLabel(`web-amoreg:workflows:${key}`));
});

export const pairKey = (organizationId: string, id: string) => `${organizationId}\u0000${id}`;

const detailAtom = Atom.family((key: string) => {
  const [organizationId = "", workflowId = ""] = key.split("\u0000");
  return complianceQueryAtom(
    organizationId,
    organizationId && workflowId
      ? readWorkflows(workflowsUrl("workflow", { organizationId, workflowId }), decodeDetail)
      : none<WorkflowDetail>(),
  ).pipe(Atom.withLabel(`web-amoreg:workflow:${workflowId}`));
});

const runAtom = Atom.family((key: string) => {
  const [organizationId = "", runId = ""] = key.split("\u0000");
  return complianceQueryAtom(
    organizationId,
    organizationId && runId
      ? readWorkflows(workflowsUrl("run", { organizationId, runId }), (json) => decodeRun(json).run)
      : none<WorkflowRun>(),
  ).pipe(Atom.withLabel(`web-amoreg:workflow-run:${runId}`));
});

export interface WorkflowView<A> {
  /** Null until the first answer. */
  readonly read: WorkflowRead<A> | null;
  readonly isPending: boolean;
  readonly reload: () => void;
}

export function useWorkflowAtom<A>(atom: Atom.Atom<AsyncResult.AsyncResult<WorkflowRead<A>, never>>): WorkflowView<A> {
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const reload = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return { read: value._tag === "Some" ? value.value : null, isPending: result.waiting, reload };
}

export const useWorkflows = (organizationId: string | null, spaceId?: string) =>
  useWorkflowAtom(useMemo(() => listAtom(listKey(organizationId ?? "", spaceId)), [organizationId, spaceId]));

export const useWorkflow = (organizationId: string | null, workflowId: string) =>
  useWorkflowAtom(useMemo(() => detailAtom(pairKey(organizationId ?? "", workflowId)), [organizationId, workflowId]));

/** How often a page holding an active run re-reads it, should a stream event be missed. */
export const ACTIVE_RUN_REFRESH_MS = 4_000;

/**
 * Whether a run can still change for its reader. An approver's partial view
 * (`sight`) never says how the run went on, so it is active only while one
 * of their own steps waits or runs (a Send back reruns them).
 */
export const isActiveRun = (run: Pick<WorkflowRun, "status" | "sight" | "steps"> | null | undefined) =>
  run?.sight === "approver"
    ? run.status === "waiting" || run.steps.some((step) => step.status === "running")
    : run?.status === "queued" || run?.status === "running" || run?.status === "waiting";

/** One run; while it is active it also re-reads on a slow timer. `runId` null reads nothing. */
export function useWorkflowRun(organizationId: string | null, runId: string | null): WorkflowView<WorkflowRun> {
  const atom = useMemo(() => runAtom(pairKey(organizationId ?? "", runId ?? "")), [organizationId, runId]);
  const view = useWorkflowAtom(atom);
  const active = view.read?.kind === "ready" && isActiveRun(view.read.value);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => appAtomRegistry.refresh(atom), ACTIVE_RUN_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [active, atom]);
  return view;
}

/** Re-read what this organization's Workflows pages show (after a write). */
export function refreshWorkflows(
  organizationId: string,
  ids: { readonly workflowId?: string; readonly runId?: string; readonly spaceId?: string | null } = {},
): void {
  appAtomRegistry.refresh(listAtom(listKey(organizationId, undefined)));
  if (ids.spaceId) appAtomRegistry.refresh(listAtom(listKey(organizationId, ids.spaceId)));
  if (ids.workflowId) appAtomRegistry.refresh(detailAtom(pairKey(organizationId, ids.workflowId)));
  if (ids.runId) appAtomRegistry.refresh(runAtom(pairKey(organizationId, ids.runId)));
}

/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */

export type WorkflowWrite<A> =
  | { readonly ok: true; readonly value: A }
  | { readonly ok: false; readonly message: string; readonly status: number; readonly body: unknown };

/** The server's own sentence for a refused write, or the fallback. */
export function workflowErrorMessage(body: unknown, fallback: string): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.trim().length > 0 && message.length < 300 ? message : fallback;
}

export async function writeWorkflows<A>(
  method: "POST" | "DELETE",
  url: string,
  body: unknown,
  decode: (json: unknown) => A,
  fallback: string,
  /** Give up after this long, as a failure; none waits as long as the request takes. */
  timeoutMs?: number,
): Promise<WorkflowWrite<A>> {
  const abort = new AbortController();
  const timer = timeoutMs === undefined ? undefined : setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method,
      credentials: "include",
      headers: { "content-type": "application/json" },
      signal: abort.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      return { ok: false, message: workflowErrorMessage(parsed, fallback), status: response.status, body: parsed };
    }
    return { ok: true, value: decode(parsed) };
  } catch {
    return { ok: false, message: fallback, status: 0, body: null };
  } finally {
    clearTimeout(timer);
  }
}

export const createWorkflow = (
  organizationId: string,
  input: { readonly name: string; readonly description?: string; readonly spaceId?: string | null },
) =>
  writeWorkflows("POST", workflowsUrl("list", { organizationId }), input, (json) => decodeWorkflow(json).workflow, "The workflow could not be created. Try again.").then(
    (outcome) => {
      if (outcome.ok) refreshWorkflows(organizationId, { spaceId: input.spaceId ?? null });
      return outcome;
    },
  );

export type SaveWorkflowOutcome =
  | { readonly kind: "saved"; readonly workflow: Workflow }
  | { readonly kind: "conflict"; readonly message: string; readonly latest: Workflow | null }
  | { readonly kind: "failed"; readonly message: string };

/**
 * Saves the graph (and name) the editor holds, naming the version it was
 * edited from. `saveId` makes sending the same save again safe: a repeat is
 * answered with the version the first one made.
 */
export async function saveWorkflow(
  organizationId: string,
  workflowId: string,
  input: {
    readonly expectedGraphVersion: number;
    readonly saveId: string;
    readonly graph?: WorkflowGraph;
    readonly name?: string;
    readonly description?: string | null;
  },
): Promise<SaveWorkflowOutcome> {
  const outcome = await writeWorkflows(
    "POST",
    workflowsUrl("workflow", { organizationId, workflowId }),
    input,
    (json) => decodeWorkflow(json).workflow,
    "The workflow could not be saved. Try again.",
  );
  if (outcome.ok) {
    refreshWorkflows(organizationId);
    return { kind: "saved", workflow: outcome.value };
  }
  // Locked since it was opened (AMO-305): read it again, so the page shows the lock and stops editing.
  if (outcome.status === 423) refreshWorkflows(organizationId, { workflowId });
  if (outcome.status === 409) {
    let latest: Workflow | null = null;
    try {
      latest = decodeWorkflow(outcome.body).workflow;
    } catch {
      latest = null;
    }
    return { kind: "conflict", message: outcome.message, latest };
  }
  return { kind: "failed", message: outcome.message };
}

export const deleteWorkflow = (organizationId: string, workflowId: string) =>
  writeWorkflows("DELETE", workflowsUrl("workflow", { organizationId, workflowId }), undefined, () => true, "The workflow could not be deleted.").then(
    (outcome) => {
      if (outcome.ok) refreshWorkflows(organizationId);
      return outcome;
    },
  );

/**
 * Turns a workflow's schedule on, changes it (the caller becomes the person
 * each scheduled run acts as) or, with `rule: null`, off.
 */
export const setWorkflowSchedule = (organizationId: string, workflowId: string, request: WorkflowScheduleRequest) =>
  writeWorkflows(
    "POST",
    workflowsUrl("workflow/schedule", { organizationId, workflowId }),
    request,
    (json) => decodeSchedule(json).schedule,
    request.rule === null ? "The schedule could not be turned off. Try again." : "The schedule could not be saved. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshWorkflows(organizationId, { workflowId });
    return outcome;
  });

export type StartRunOutcome =
  | { readonly kind: "started"; readonly run: WorkflowRun }
  | { readonly kind: "invalid"; readonly message: string; readonly issues: ReadonlyArray<GraphIssue> }
  | { readonly kind: "failed"; readonly message: string };

export async function startWorkflowRun(
  organizationId: string,
  workflowId: string,
  input: string,
  files: ReadonlyArray<WorkflowRunFile> = [],
): Promise<StartRunOutcome> {
  const outcome = await writeWorkflows(
    "POST",
    workflowsUrl("workflow/run", { organizationId, workflowId }),
    { ...(input.trim() ? { input } : {}), ...(files.length > 0 ? { files } : {}) },
    (json) => decodeRun(json).run,
    "The run could not start. Try again.",
  );
  refreshWorkflows(organizationId, { workflowId });
  if (outcome.ok) return { kind: "started", run: outcome.value };
  if (outcome.status === 422) {
    try {
      const refused = decodeIssues(outcome.body);
      return { kind: "invalid", message: refused.message, issues: refused.issues };
    } catch {
      // Fall through to a plain failure.
    }
  }
  return { kind: "failed", message: outcome.message };
}

export const cancelWorkflowRun = (organizationId: string, workflowId: string, runId: string) =>
  writeWorkflows("POST", workflowsUrl("run/cancel", { organizationId, runId }), undefined, (json) => decodeRun(json).run, "The run could not be stopped. Try again.").then(
    (outcome) => {
      refreshWorkflows(organizationId, { workflowId, runId });
      return outcome;
    },
  );

/**
 * Runs a step, and everything after it, again. With `withCurrentSettings`,
 * those steps take the workflow's settings as they are now (AMO-308).
 */
export const retryWorkflowStep = (organizationId: string, workflowId: string, runId: string, nodeId: string, withCurrentSettings = false) =>
  writeWorkflows(
    "POST",
    workflowsUrl("run/retry", { organizationId, runId, nodeId, ...(withCurrentSettings ? { current: "1" } : {}) }),
    undefined,
    (json) => decodeRun(json).run,
    "The step could not be retried. Try again.",
  ).then((outcome) => {
    refreshWorkflows(organizationId, { workflowId, runId });
    return outcome;
  });

const decodeSuggestions = Schema.decodeUnknownSync(WorkflowSuggestFieldsResponse);
const decodeSharing = Schema.decodeUnknownSync(Schema.Struct({ sharing: WorkflowSharing }));

/** Shares a workflow (its owner only): what everyone may do, and the members it is shared with. */
export const setWorkflowSharing = (organizationId: string, workflowId: string, sharing: typeof WorkflowSharing.Type) =>
  writeWorkflows(
    "POST",
    workflowsUrl("workflow/sharing", { organizationId, workflowId }),
    sharing,
    (json) => decodeSharing(json).sharing,
    "The sharing could not be saved. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshWorkflows(organizationId, { workflowId });
    return outcome;
  });
const decodeLock = Schema.decodeUnknownSync(WorkflowLockResponse);

/** Locks a workflow, with an optional reason, or unlocks it (its owner and editors; AMO-305). */
export const setWorkflowLock = (organizationId: string, workflowId: string, request: WorkflowLockRequest) =>
  writeWorkflows(
    "POST",
    workflowsUrl("workflow/lock", { organizationId, workflowId }),
    request,
    decodeLock,
    request.locked ? "The workflow could not be locked. Try again." : "The workflow could not be unlocked. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshWorkflows(organizationId, { workflowId });
    return outcome;
  });
const decodeBrowse = Schema.decodeUnknownSync(WorkflowIntakeBrowseResponse);

/**
 * What an Intake step can pick in SharePoint or Google Drive: the allowed
 * places (no location or folder), or one folder's files and folders. A store
 * that is not connected answers with the server's reason.
 */
export async function browseIntakeStore(
  organizationId: string,
  input: { readonly kind: "sharepoint" | "googledrive"; readonly location?: string; readonly folder?: string },
): Promise<WorkflowWrite<IntakeBrowseResponse["items"]>> {
  const params: Record<string, string> = { organizationId, kind: input.kind };
  if (input.location) params.location = input.location;
  if (input.folder) params.folder = input.folder;
  try {
    const response = await fetch(workflowsUrl("intake/browse", params), { credentials: "include" });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) return { ok: false, message: workflowErrorMessage(body, "This place could not be listed."), status: response.status, body };
    return { ok: true, value: decodeBrowse(body).items };
  } catch {
    return { ok: false, message: "This place could not be listed.", status: 0, body: null };
  }
}

const decodeMailbox = Schema.decodeUnknownSync(WorkflowMailboxAccessResponse);

/** The viewer's own mailbox permissions on a workflow, and their connected Gmail and Outlook. */
export async function readWorkflowMailbox(
  organizationId: string,
  workflowId: string,
): Promise<WorkflowWrite<typeof WorkflowMailboxAccessResponse.Type>> {
  try {
    const response = await fetch(workflowsUrl("mailbox", { organizationId, workflowId }), { credentials: "include" });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) return { ok: false, message: workflowErrorMessage(body, "Your mailbox permissions could not be read."), status: response.status, body };
    return { ok: true, value: decodeMailbox(body) };
  } catch {
    return { ok: false, message: "Your mailbox permissions could not be read.", status: 0, body: null };
  }
}

/**
 * Lets this workflow read the viewer's own mailbox (for every run, or one
 * waiting run of theirs), or withdraws it. A run waiting for it goes on.
 */
export const setWorkflowMailbox = (organizationId: string, workflowId: string, request: WorkflowMailboxAccessRequest) =>
  writeWorkflows(
    "POST",
    workflowsUrl("mailbox", { organizationId, workflowId }),
    request,
    (json) => decodeMailbox(json),
    "Your mailbox permission could not be saved. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshWorkflows(organizationId, { workflowId, ...(request.runId ? { runId: request.runId } : {}) });
    return outcome;
  });

/** Fields for an Ingestion step suggested from a sample file's text, for the person to edit; nothing is kept. */
export const suggestIngestionFields = (organizationId: string, request: WorkflowSuggestFieldsRequest) =>
  writeWorkflows(
    "POST",
    workflowsUrl("ingestion/suggest", { organizationId }),
    request,
    (json) => decodeSuggestions(json).fields,
    "No fields could be suggested from this file. Try again, or list them yourself.",
  );

/**
 * An approver's decision on a waiting Approval step: approve, reject with a
 * reason, or feedback naming an earlier step to run again from. Answers with
 * the run as it now stands, or the server's plain reason for refusing.
 */
export const decideWorkflowApproval = (
  organizationId: string,
  workflowId: string,
  runId: string,
  nodeId: string,
  request: WorkflowDecideRequest,
) =>
  writeWorkflows(
    "POST",
    workflowsUrl("run/decide", { organizationId, runId, nodeId }),
    request,
    (json) => decodeRun(json).run,
    "The decision could not be saved. Try again.",
  ).then((outcome) => {
    refreshWorkflows(organizationId, { workflowId, runId });
    return outcome;
  });

/* ------------------------------------------------------------------ */
/* One open chart                                                      */
/* ------------------------------------------------------------------ */

const decodeChats = Schema.decodeUnknownSync(WorkflowChatsResponse);
const decodeChat = Schema.decodeUnknownSync(WorkflowChat);

/** The person's chats opened from this workflow's Ask eve panel, newest first. */
export const readWorkflowChats = (organizationId: string, workflowId: string) =>
  Effect.runPromise(
    readWorkflows(workflowsUrl("workflow/chats", { organizationId, workflowId }), (json) => decodeChats(json).chats),
  );

/** How long a chat's binding may take before it counts as failed. */
export const BIND_CHAT_TIMEOUT_MS = 10_000;

/** Binds a chat to the workflow, so its turns are told which chart they are about. */
export const bindWorkflowChat = (organizationId: string, workflowId: string, threadId: string) =>
  writeWorkflows(
    "POST",
    workflowsUrl("workflow/chats", { organizationId, workflowId }),
    { threadId },
    decodeChat,
    "The chat could not be linked to this workflow.",
    BIND_CHAT_TIMEOUT_MS,
  );

/** How long after the last edit the chart saves itself. */
export const AUTOSAVE_DELAY_MS = 900;

const decodeRevisionsSince = Schema.decodeUnknownExit(WorkflowRevisionsSinceResult);

const contentOf = (workflow: Workflow): Content => ({ graph: workflow.graph, name: workflow.name, description: workflow.description });

export interface WorkflowDocView {
  readonly graph: WorkflowGraph;
  readonly name: string;
  readonly status: SaveStatus;
  /** Why the last save failed, while it is the latest word. */
  readonly saveError: string | null;
  /** The graph as it is this instant, for handlers that read it between renders. */
  readonly currentGraph: () => WorkflowGraph;
  /** An edit the person made: undoable, and edits under one `key` close together count as one. */
  readonly edit: (update: (graph: WorkflowGraph) => WorkflowGraph, key?: string | null) => void;
  /** A change that is not the person's to undo (an old chart's one-time relayout). */
  readonly adjust: (update: (graph: WorkflowGraph) => WorkflowGraph) => void;
  readonly rename: (name: string) => void;
  readonly undo: () => void;
  /** Take back one agent request made for the person, as ⌘Z does for the newest. */
  readonly undoRequest: (group: string) => void;
  readonly redo: () => void;
  readonly subscribe: (listener: DocListener) => () => void;
  readonly retry: () => void;
  /** A save is on its way to the server. */
  readonly saving: boolean;
  /** Drops unsaved edits for the confirmed version, unless a save is in flight; true when there were any. */
  readonly discardUnsaved: () => boolean;
}

type SaveEffect = Extract<WorkflowDocEffect, { readonly type: "save" }>;

/** What one open chart's copy does in the world; `useWorkflowDoc` gives it the chart's current props. */
export interface DocIo {
  readonly canEdit: boolean;
  readonly save: (save: SaveEffect) => Promise<WorkflowDocEvent>;
  /** The workflow as the server has it now, or null when it cannot be read. */
  readonly readLatest: () => Promise<{ readonly version: number; readonly content: Content } | null>;
  /** Re-read the workflow through the page's read, which comes back as a `read`. */
  readonly reread: () => void;
  readonly revisionsSince: WorkflowPresenceView["revisionsSince"];
  /** The person's Undo and Redo of requests, or null when they cannot be read. */
  readonly readRequests: () => Promise<WorkflowRequestPointers | null>;
  readonly revertRequest: (revert: Extract<WorkflowDocEffect, { readonly type: "revert-request" }>) => Promise<WorkflowDocEvent>;
  readonly onNotice: (notice: WorkflowDocNotice) => void;
}

/** Told of every step of the doc that changed it, with the state before and after. */
export type DocListener = (before: WorkflowDocState, after: WorkflowDocState) => void;

/**
 * The reducer with its IO: saves, catch-ups, reads and notices. `io` is read
 * when an effect runs, so it always names the chart's current props. After
 * `close` a late reply still updates the copy, and the edits left unsaved
 * are saved, merging over a newer version once.
 */
export function openWorkflowDoc(initial: WorkflowDocState, io: () => DocIo, onChange: (state: WorkflowDocState) => void) {
  let state = initial;
  let open = true;
  let inFlight: Promise<unknown> = Promise.resolve();
  const listeners = new Set<DocListener>();

  const dispatch = (event: WorkflowDocEvent) => {
    const before = state;
    const step = reduceWorkflowDoc(state, event);
    state = step.state;
    if (!open) return;
    onChange(state);
    for (const effect of step.effects) run(effect);
    if (state === before) return;
    for (const listener of listeners) {
      try {
        listener(before, state);
      } catch (error) {
        queueMicrotask(() => {
          throw error;
        });
      }
    }
  };

  const run = (effect: WorkflowDocEffect) => {
    const { canEdit, save, reread, revisionsSince, readRequests, revertRequest, onNotice } = io();
    switch (effect.type) {
      case "save":
        inFlight = save(effect).then(dispatch);
        return;
      case "catch-up":
        void revisionsSince(effect.since).then((outcome) => {
          const at = Date.now();
          if (outcome.kind === "failed") return dispatch({ type: "catch-up-failed", retryAfterMs: outcome.retryAfterMs, at });
          const decoded = decodeRevisionsSince(outcome.result);
          if (decoded._tag === "Failure") {
            // Unreadable: the workflow read is the fallback.
            reread();
            return dispatch({ type: "catch-up-failed", retryAfterMs: null, at });
          }
          const result = decoded.value;
          dispatch(
            result.kind === "revisions"
              ? { type: "caught-up", revisions: result.revisions, at }
              : { type: "head-taken", version: result.version, content: result.content, at },
          );
        });
        return;
      case "read":
        reread();
        return;
      case "read-requests":
        void (canEdit ? readRequests() : Promise.resolve(NO_REQUESTS)).then((pointers) =>
          dispatch({ type: "requests-read", pointers, at: Date.now() }),
        );
        return;
      case "revert-request":
        void revertRequest(effect).then(dispatch);
        return;
      case "notice":
        // Notices are about the person's own edits: a chart they may only run has none.
        if (canEdit) onNotice(effect.notice);
        return;
    }
  };

  const flush = async () => {
    const { save, readLatest, onNotice } = io();
    for (let attempt = 0; attempt < 3; attempt += 1) {
      // A 409 that named no newer version: read it here, since no page will.
      if (state.save.kind === "behind") {
        const latest = await readLatest();
        if (latest === null) break;
        state = reduceWorkflowDoc(state, { type: "read", ...latest, at: Date.now() }).state;
      }
      const step = reduceWorkflowDoc(state, { type: "save-due", saveId: crypto.randomUUID() });
      state = step.state;
      const sending = step.effects.find((effect) => effect.type === "save");
      if (!sending) break;
      const reply = await save(sending);
      state = reduceWorkflowDoc(state, reply).state;
      if (reply.type === "save-failed") break;
    }
    if (unsavedBody(state) !== null) onNotice({ kind: "left-unsaved", workflow: state.confirmed.content.name });
  };

  return {
    dispatch,
    current: () => state,
    subscribe: (listener: DocListener) => {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    reopen: () => {
      open = true;
    },
    /** Resolves once the edits left unsaved are saved, or could not be. */
    close: (): Promise<void> => {
      open = false;
      return io().canEdit ? inFlight.then(flush) : Promise.resolve();
    },
  };
}

/**
 * One open chart's copy of `workflow`: edits, saves (a moment after the last
 * edit, one at a time, each under a fresh `saveId`), and every newer version
 * merged in as it arrives over `presence`. Leaving with edits unsaved saves
 * them on the way out. A chart the person may only run never saves.
 */
export function useWorkflowDoc(input: {
  readonly organizationId: string;
  readonly workflow: Workflow;
  readonly canEdit: boolean;
  readonly presence: Pick<WorkflowPresenceView, "onWorkflowEvent" | "revisionsSince">;
  readonly onNotice: (notice: WorkflowDocNotice) => void;
}): WorkflowDocView {
  const { organizationId, workflow, canEdit } = input;
  const { onWorkflowEvent } = input.presence;
  const ioOf = (): DocIo => ({
    canEdit: input.canEdit,
    save: (save) => send(organizationId, workflow.id, save),
    readLatest: () => readLatest(organizationId, workflow.id),
    reread: () => refreshWorkflows(organizationId, { workflowId: workflow.id }),
    revisionsSince: input.presence.revisionsSince,
    readRequests: () => readRequests(organizationId, workflow.id),
    revertRequest: (revert) => sendRevert(organizationId, workflow.id, revert),
    onNotice: input.onNotice,
  });
  const io = useRef<DocIo>(ioOf());
  io.current = ioOf();
  const [state, setState] = useState(() => initialDoc(workflow.graphVersion, contentOf(workflow)));
  const [doc] = useState(() => openWorkflowDoc(state, () => io.current, setState));
  const { dispatch } = doc;

  useEffect(() => {
    doc.reopen();
    return () => void doc.close();
  }, [doc]);

  useEffect(
    () =>
      onWorkflowEvent((event) => {
        const at = Date.now();
        if (event.type === "sync" && event.version !== null) dispatch({ type: "synced", version: event.version, you: event.you, at });
        else if (event.type === "revision") dispatch({ type: "revision", revision: event.revision, at });
      }),
    [onWorkflowEvent, dispatch],
  );

  // The read moved on (the compliance stream, or the fallback after an unreadable catch-up).
  useEffect(() => {
    dispatch({ type: "read", version: workflow.graphVersion, content: contentOf(workflow), at: Date.now() });
  }, [workflow, dispatch]);

  // Save a moment after the last edit: a colleague's version merging in does not push it back.
  const autosave = useRef<{ readonly timer: number; readonly delay: number } | null>(null);
  const stopAutosave = useCallback(() => {
    if (autosave.current) window.clearTimeout(autosave.current.timer);
    autosave.current = null;
  }, []);
  const delay = canEdit ? saveDelay(state, AUTOSAVE_DELAY_MS) : null;
  useEffect(() => {
    if (delay === null) return stopAutosave();
    if (autosave.current && autosave.current.delay <= delay) return;
    stopAutosave();
    const timer = window.setTimeout(() => {
      autosave.current = null;
      dispatch({ type: "save-due", saveId: crypto.randomUUID() });
    }, delay);
    autosave.current = { timer, delay };
  }, [delay, state, dispatch, stopAutosave]);
  useEffect(() => stopAutosave, [stopAutosave]);

  const wake = nextTick(state);
  useEffect(() => {
    if (wake === null) return;
    const timer = window.setTimeout(() => dispatch({ type: "tick", at: Date.now() }), Math.max(0, wake - Date.now()));
    return () => window.clearTimeout(timer);
  }, [wake, dispatch]);

  // The person's own change restarts the autosave delay; one that changed nothing (an empty undo) leaves it running.
  const byPerson = useCallback(
    (event: WorkflowDocEvent) => {
      const before = doc.current();
      dispatch(event);
      if (doc.current() !== before) stopAutosave();
    },
    [doc, dispatch, stopAutosave],
  );
  const status = canEdit ? saveStatus(state) : "saved";
  return {
    graph: state.local.graph,
    name: state.local.name,
    status,
    saveError: status === "error" && state.save.kind === "failed" ? state.save.message : null,
    currentGraph: useCallback(() => doc.current().local.graph, [doc]),
    edit: useCallback(
      (update: (graph: WorkflowGraph) => WorkflowGraph, key: string | null = null) =>
        byPerson({ type: "edited", update: onGraph(update), key, at: Date.now() }),
      [byPerson],
    ),
    adjust: useCallback((update: (graph: WorkflowGraph) => WorkflowGraph) => dispatch({ type: "adjusted", update: onGraph(update) }), [dispatch]),
    rename: useCallback(
      (name: string) => byPerson({ type: "adjusted", update: (content) => (content.name === name ? content : { ...content, name }) }),
      [byPerson],
    ),
    undo: useCallback(() => byPerson({ type: "undo", saveId: crypto.randomUUID() }), [byPerson]),
    undoRequest: useCallback((group: string) => byPerson({ type: "undo", saveId: crypto.randomUUID(), group }), [byPerson]),
    redo: useCallback(() => byPerson({ type: "redo", saveId: crypto.randomUUID() }), [byPerson]),
    retry: useCallback(() => dispatch({ type: "retry" }), [dispatch]),
    subscribe: doc.subscribe,
    saving: state.save.kind === "saving",
    discardUnsaved: useCallback(() => {
      const now = doc.current();
      if (now.save.kind === "saving") return false;
      const unsaved = unsavedBody(now) !== null;
      if (unsaved || now.local !== now.confirmed.content) dispatch({ type: "discarded" });
      return unsaved;
    }, [doc, dispatch]),
  };
}

const onGraph =
  (update: (graph: WorkflowGraph) => WorkflowGraph) =>
  (content: Content): Content => {
    const graph = update(content.graph);
    return graph === content.graph ? content : { ...content, graph };
  };

/** The workflow as the server has it now, past any cached read. */
async function readLatest(organizationId: string, workflowId: string): Promise<{ readonly version: number; readonly content: Content } | null> {
  const read = await Effect.runPromise(readWorkflows(workflowsUrl("workflow", { organizationId, workflowId }), decodeDetail));
  return read.kind === "ready" ? { version: read.value.workflow.graphVersion, content: contentOf(read.value.workflow) } : null;
}

const NO_REQUESTS: WorkflowRequestPointers = { undo: null, redo: null };
/** ⌘Z waits for a read of the pointers on its way: never longer than this. */
const REQUESTS_READ_TIMEOUT = "5 seconds";
const decodePointers = Schema.decodeUnknownSync(WorkflowRequestPointers);
const decodeUndoResult = Schema.decodeUnknownSync(WorkflowUndoResult);

async function readRequests(organizationId: string, workflowId: string): Promise<WorkflowRequestPointers | null> {
  const read = await Effect.runPromise(
    readWorkflows(workflowsUrl("workflow/requests", { organizationId, workflowId }), decodePointers).pipe(
      Effect.timeoutOrElse({ duration: REQUESTS_READ_TIMEOUT, orElse: () => Effect.succeed({ kind: "unavailable" } as const) }),
    ),
  );
  return read.kind === "ready" ? read.value : null;
}

async function sendRevert(
  organizationId: string,
  workflowId: string,
  revert: Extract<WorkflowDocEffect, { readonly type: "revert-request" }>,
): Promise<WorkflowDocEvent> {
  const outcome = await writeWorkflows(
    "POST",
    workflowsUrl(`workflow/${revert.direction}`, { organizationId, workflowId }),
    { group: revert.group, saveId: revert.saveId },
    decodeUndoResult,
    `That could not be ${revert.direction === "undo" ? "undone" : "redone"}. Try again.`,
  );
  return {
    type: "request-answered",
    saveId: revert.saveId,
    answer: outcome.ok ? outcome.value : { kind: "failed", message: outcome.message },
    at: Date.now(),
  };
}

/** One save, answered as the reducer's event. */
async function send(organizationId: string, workflowId: string, save: SaveEffect): Promise<WorkflowDocEvent> {
  const outcome = await saveWorkflow(organizationId, workflowId, {
    expectedGraphVersion: save.expectedVersion,
    saveId: save.saveId,
    graph: save.body.graph,
    name: save.body.name,
  });
  const at = Date.now();
  switch (outcome.kind) {
    case "saved":
      return { type: "save-succeeded", saveId: save.saveId, version: outcome.workflow.graphVersion, content: contentOf(outcome.workflow), at };
    case "conflict":
      return {
        type: "save-conflicted",
        saveId: save.saveId,
        latest: outcome.latest ? { version: outcome.latest.graphVersion, content: contentOf(outcome.latest) } : null,
        at,
      };
    case "failed":
      return { type: "save-failed", saveId: save.saveId, message: outcome.message };
  }
}
